import {
  Container,
  IconButton,
  Input,
  Spinner,
  Stack,
  Table,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { LuBan, LuTrash, LuUserPlus } from "react-icons/lu";
import { z } from "zod";

import {
  Workspace,
  addPatronEmailBlock,
  addPatronSyncExemption,
  deletePatronEmailBlock,
  deletePatronSyncExemption,
  getPatronEmailBlocks,
  getPatronSyncExemptions,
} from "../api";
import type { PatronEmailBlock, PatronSyncExemption } from "../api";
import { Button } from "../components/chakra/button";
import {
  DialogActionTrigger,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../components/chakra/dialog";
import { Field } from "../components/chakra/field";
import { SectionHeader } from "../components/section-header";
import { useLoading, useToaster, useWorkspace } from "../hooks";
import { SettingsLayout } from "../layouts/settings";

const formSchema = z.object({
  email: z.email(),
});

type FormData = z.infer<typeof formSchema>;

type EmailListItem = PatronEmailBlock | PatronSyncExemption;

export const PatronSettingsPage = () => {
  const workspace = useWorkspace()!;
  return (
    <SettingsLayout>
      <Container pt="8" maxW="4xl">
        <Stack>
          <EmailListSection
            workspace={workspace}
            title="Patron Sync Exemptions"
            subtitle="Patrons on this list are skipped during entitlement sync. Manage their entitlements manually."
            triggerLabel="Add Patron"
            triggerIcon={<LuUserPlus />}
            getItems={getPatronSyncExemptions}
            addItem={addPatronSyncExemption}
            removeItem={deletePatronSyncExemption}
          />
          <EmailListSection
            workspace={workspace}
            title="E-mail Blocklist"
            subtitle="E-mail addresses on this list are blocked from accessing this workspace's uploads, current and future, until they're removed from the list."
            triggerLabel="Block E-mail Address"
            triggerIcon={<LuBan />}
            getItems={getPatronEmailBlocks}
            addItem={addPatronEmailBlock}
            removeItem={deletePatronEmailBlock}
          />
        </Stack>
      </Container>
    </SettingsLayout>
  );
};

interface EmailListSectionProps {
  workspace: Workspace;
  title: string;
  subtitle: string;
  triggerLabel: string;
  triggerIcon: React.ReactNode;
  getItems: (workspace: Workspace) => Promise<EmailListItem[]>;
  addItem: (workspace: Workspace, data: { email: string }) => Promise<void>;
  removeItem: (workspace: Workspace, id: number) => Promise<void>;
}

const EmailListSection = (props: EmailListSectionProps) => {
  const {
    workspace,
    title,
    subtitle,
    triggerLabel,
    triggerIcon,
    getItems,
    addItem,
    removeItem,
  } = props;
  const [items, setItems] = React.useState<EmailListItem[]>([]);
  const [loading, withLoading] = useLoading();
  const loadItems = React.useCallback(async () => {
    await withLoading(async () => {
      setItems(await getItems(workspace));
    });
  }, [getItems, withLoading, workspace]);
  React.useEffect(() => {
    loadItems();
  }, [loadItems]);

  const withToast = useToaster();
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [addDialogLoading, withAddDialogLoading] = useLoading();
  const onAdd = React.useCallback(
    async (data: FormData) => {
      await withAddDialogLoading(async () => {
        await withToast(async () => {
          await addItem(workspace, data);
          await loadItems();
          setAddDialogOpen(false);
        });
      });
    },
    [addItem, loadItems, workspace, withAddDialogLoading, withToast],
  );
  const onRemove = React.useCallback(
    async (id: number) => {
      await removeItem(workspace, id);
    },
    [removeItem, workspace],
  );

  return (
    <Stack>
      <SectionHeader title={title} subtitle={subtitle} />

      <Table.Root borderRadius="sm" maxW="xl" p="2" variant="outline">
        <Table.Body>
          {loading ? (
            <Table.Row>
              <Table.Cell>
                <Spinner m="2" size="xl" />
              </Table.Cell>
            </Table.Row>
          ) : (
            <>
              <Table.Row>
                <Table.Cell colSpan={2} textAlign="end">
                  <DialogRoot
                    size="sm"
                    open={addDialogOpen}
                    onOpenChange={(e) => setAddDialogOpen(e.open)}
                  >
                    <DialogBackdrop />
                    <DialogTrigger asChild>
                      <Button size="xs">
                        {triggerIcon} {triggerLabel}
                      </Button>
                    </DialogTrigger>
                    <AddItemDialogContent
                      title={triggerLabel}
                      loading={addDialogLoading}
                      onAdd={onAdd}
                    />
                  </DialogRoot>
                </Table.Cell>
              </Table.Row>

              {items.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  listName={title}
                  onRemove={onRemove}
                  onChange={() => loadItems()}
                />
              ))}
            </>
          )}
        </Table.Body>
      </Table.Root>
    </Stack>
  );
};

interface ItemRowProps {
  item: EmailListItem;
  listName: string;
  onRemove: (id: number) => Promise<void>;
  onChange?: () => void;
}

const ItemRow = (props: ItemRowProps) => {
  const { item, listName, onRemove, onChange } = props;
  const withToast = useToaster();
  const onDelete = React.useCallback(async () => {
    if (confirm(`Remove ${item.email} from ${listName}?`)) {
      await withToast(async () => {
        await onRemove(item.id);
      });
      if (onChange !== undefined) {
        onChange();
      }
    }
  }, [item, listName, onChange, onRemove, withToast]);

  return (
    <Table.Row>
      <Table.Cell>{item.email}</Table.Cell>
      <Table.Cell textAlign="end">
        <IconButton
          aria-label="Delete"
          size="sm"
          borderRadius="full"
          variant="ghost"
          onClick={onDelete}
        >
          <LuTrash />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
};

interface AddItemDialogContentProps {
  title: string;
  loading: boolean;
  onAdd: (data: FormData) => void;
}

const AddItemDialogContent = (props: AddItemDialogContentProps) => {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });
  const onAdd = handleSubmit(props.onAdd);
  return (
    <DialogContent>
      <form onSubmit={onAdd}>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field
            label="Email Address"
            invalid={!!errors.email}
            errorText={errors.email?.message}
          >
            <Input type="email" {...register("email")} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline">Cancel</Button>
          </DialogActionTrigger>
          <Button loading={props.loading} type="submit">
            {props.title}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
